/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_construction_purposes-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_construction_purposes-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_construction_purposes-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, code, name, sort_order, created_at, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_construction_purposes-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_construction_purposes-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_construction_purposes.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_construction_purposes-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupConstructionPurposesClient, LookupConstructionPurposesQueryOptions } from '../generated_clients/generated-lookup_construction_purposes-api-client.js';
import {
  LookupConstructionPurposesInsert,
  LookupConstructionPurposesUpdate,
  LookupConstructionPurposesFieldConfig
} from '../../types/generated_schemas/generated-lookup_construction_purposes-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_construction_purposes_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupConstructionPurposesKeys = {
  all: ['lookup_construction_purposes'] as const,
  lists: () => [...lookupConstructionPurposesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupConstructionPurposesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupConstructionPurposesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupConstructionPurposesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupConstructionPurposesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupConstructionPurposesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupConstructionPurposesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupConstructionPurposesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupConstructionPurposesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupConstructionPurposesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupConstructionPurposesClient();
};

/**
 * Hook to get all lookup_construction_purposes with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_construction_purposes data
 */
export const useLookupConstructionPurposesList = (options?: LookupConstructionPurposesQueryOptions) => {
  const client = useLookupConstructionPurposesClient();
  
  return useQuery({
    queryKey: lookupConstructionPurposesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_construction_purpose by ID
 *
 * @param id - The lookup_construction_purpose ID
 * @returns React Query result with single lookup_construction_purpose data
 */
export const useLookupConstructionPurposes = (id: string) => {
  const client = useLookupConstructionPurposesClient();
  
  return useQuery({
    queryKey: lookupConstructionPurposesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_construction_purposes
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_construction_purposes_business_hooks/
 */

/**
 * Hook to create a new lookup_construction_purpose
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, code, name, sort_order, created_at, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_construction_purpose
 */
export const useCreateLookupConstructionPurposes = () => {
  const client = useLookupConstructionPurposesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupConstructionPurposesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupConstructionPurposesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_construction_purpose
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_construction_purpose
 */
export const useUpdateLookupConstructionPurposes = () => {
  const client = useLookupConstructionPurposesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupConstructionPurposesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupConstructionPurposesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupConstructionPurposesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_construction_purpose
 *
 * @returns React Query mutation for deleting lookup_construction_purpose
 */
export const useDeleteLookupConstructionPurposes = () => {
  const client = useLookupConstructionPurposesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupConstructionPurposesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_construction_purposes with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupConstructionPurposesCount = (filter?: Partial<Record<typeof LookupConstructionPurposesFieldConfig.filterable[number], any>>) => {
  const client = useLookupConstructionPurposesClient();
  
  return useQuery({
    queryKey: lookupConstructionPurposesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupConstructionPurposesFieldConfig = () => {
  return LookupConstructionPurposesFieldConfig;
};